import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DashboardCard22 from "../../partials/dashboard/DashboardCard22";
import DashboardCard22prod from "../../partials/dashboard/DashboardCard22prod";
import { PhysiciansInfo } from "../../components/physicians/physicians-info";
import { PhysicianDetailsForm } from "../../components/physicians/physicians-details-form";
import { jwtDecode } from 'jwt-decode';
import {
  HiOutlineBuildingOffice2,
  HiOutlineCube
} from "react-icons/hi2";

type JwtPayload = {
  userId: number;
  username: string;
  role: string;
};

export default function PhysiciansEdit({ className = "" }: { className?: string }): React.JSX.Element {
  const location = useLocation();
  const { item: targetPhysician } = location.state || {};
  const [user, setUser] = useState<JwtPayload | null>(null);
  const [fetchUsersCallback, setFetchUsersCallback] = useState<() => void>(() => () => { });
  const [activeTab, setActiveTab] = useState("institutions");

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      try {
        const decoded: JwtPayload = jwtDecode(token);
        setUser(decoded);
      } catch (error) {
        console.error('Error decoding the token:', error);
      }
    }
  }, []);

  const dashboardTabs = [
    {
      id: "institutions",
      label: "Instituții afiliate",
      icon: <HiOutlineBuildingOffice2 className="w-4 h-4" />,
      component: (
        <div className="w-full">
          <DashboardCard22 />
        </div>
      )
    },
    {
      id: "products",
      label: "Produse asociate",
      icon: <HiOutlineCube className="w-4 h-4" />,
      component: (
        <div className="w-full">
          <DashboardCard22prod />
        </div>
      )
    },
  ];

  const isAdmin = user?.role === "Admin";

  return (
    <div className={`w-full ${className}`}>
      {/* Top Section: Doctor Info Card & Doctor Details Form */}
      <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-12' : ''} gap-5 sm:gap-6 items-start`}>
        {/* Left: Doctor Summary Card (4 cols) */}
        <div className={isAdmin ? "lg:col-span-4 w-full" : "max-w-md mx-auto w-full"}>
          <PhysiciansInfo setFetchUsersCallback={setFetchUsersCallback} />
        </div>

        {/* Right: Doctor Details Form (Admin only, 8 cols) */}
        {isAdmin && (
          <div className="lg:col-span-8 w-full">
            <PhysicianDetailsForm onSaveChanges={fetchUsersCallback} />
          </div>
        )}
      </div>

      {/* Tabs Navigation Shell */}
      <div className="mt-6 mb-4">
        <div className="bg-white dark:bg-gray-800 border border-[#E5E8EF] dark:border-gray-700/60 rounded-xl p-1 shadow-xs grid grid-cols-1 sm:grid-cols-2 gap-1 max-w-xl">
          {dashboardTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`h-[38px] rounded-[9px] flex items-center justify-center gap-2 text-[12px] transition-all duration-150 cursor-pointer ${
                  isActive
                    ? "bg-[#F0EDFF] text-[#6755E8] font-semibold shadow-[0_1px_3px_rgba(60,50,130,0.06)] dark:bg-indigo-950/70 dark:text-indigo-300"
                    : "text-[#697386] hover:text-[#172033] hover:bg-gray-50/80 dark:text-gray-400 dark:hover:bg-gray-700/50 font-medium"
                }`}
              >
                <span className={isActive ? "text-[#6755E8] dark:text-indigo-300" : "text-[#8E97A6]"}>
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Panel Content with Smooth Animation */}
      <div key={activeTab} className="w-full animate-tabFadeIn pb-10">
        {dashboardTabs.find((t) => t.id === activeTab)?.component}
      </div>
    </div>
  );
}
